import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, Button, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import TimerRounded from '@mui/icons-material/TimerRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import { useActiveTimers } from '../api/time-tracking';
import { formatDurationClock, useElapsedSeconds } from '../lib/elapsed';
import type { ActiveTimer } from '../lib/types';
import { ColorDot } from './common';
import { EndTaskModal } from './EndTaskModal';

function ActiveTimerRow({ timer, onOpen }: { timer: ActiveTimer; onOpen: (taskKey: string) => void }) {
  const elapsed = useElapsedSeconds(timer.startedAt);
  const [ending, setEnding] = useState(false);
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 1, py: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', animation: 'qsPing 1.6s ease-in-out infinite' }} />
      <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpen(timer.taskSlug ?? timer.taskId)}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{timer.taskTitle}</Typography>
        {timer.project && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ColorDot color={timer.project.color} size={7} />
            <Typography variant="caption" color="text.secondary" noWrap>{timer.project.name}</Typography>
          </Stack>
        )}
      </Box>
      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatDurationClock(elapsed)}</Typography>
      <Button size="small" color="error" variant="outlined" startIcon={<StopRounded />} onClick={() => setEnding(true)}>Stop</Button>
      <EndTaskModal taskId={timer.taskId} taskTitle={timer.taskTitle} open={ending} onClose={() => setEnding(false)} />
    </Stack>
  );
}

/** Header button listing every timer the current user has running, with a quick "stop" for each. */
export function ActiveTimersIndicator() {
  const { data: timers = [] } = useActiveTimers();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const count = timers.length;

  return (
    <>
      <Tooltip title={count > 0 ? `Working on ${count} task${count > 1 ? 's' : ''}` : 'Time tracking'}>
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} color={count > 0 ? 'primary' : 'default'}>
          <Badge badgeContent={count} color="error">
            <TimerRounded sx={count > 0 ? { animation: 'qsPulseGlow 2s ease-in-out infinite' } : undefined} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 380, maxWidth: '92vw', p: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, fontWeight: 800 }}>Currently working on</Typography>
          {count === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <TimerRounded color="disabled" />
              <Typography variant="body2" color="text.secondary">No timers running. Start a task from its detail page to track time.</Typography>
            </Box>
          ) : (
            timers.map((t) => (
              <ActiveTimerRow
                key={t.id}
                timer={t}
                onOpen={(key) => {
                  setAnchor(null);
                  navigate(`/pma/tasks/${key}`);
                }}
              />
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}
